import {
  WebGLRenderer,
  Scene,
  PerspectiveCamera,
  DirectionalLight,
  AmbientLight,
  Vector3,
  Clock,
  Color,
  Fog,
} from 'three';
import { ChunkManager } from './terrain';
import { Player } from './player';
import { Minimap } from './minimap';
import {
  CAMERA_DISTANCE,
  CAMERA_HEIGHT,
  CAMERA_LOOK,
  CAMERA_SMOOTH,
  CAMERA_MIN_ABOVE_TERRAIN,
  WORLD_SCALE,
  TILE_SIZE,
} from './config';

const renderer = new WebGLRenderer({
  canvas: document.getElementById('game') as HTMLCanvasElement,
  antialias: true,
  powerPreference: 'high-performance',
});
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = true;
renderer.toneMapping = 3;
renderer.toneMappingExposure = 1.0;

const scene = new Scene();
scene.background = new Color(0x88aacc);
scene.fog = new Fog(
  0x88aacc,
  TILE_SIZE * WORLD_SCALE * 3,
  TILE_SIZE * WORLD_SCALE * 6,
);

const camera = new PerspectiveCamera(
  65,
  window.innerWidth / window.innerHeight,
  5,
  30000,
);
camera.position.set(0, 200, 0);

const sun = new DirectionalLight(0xfff0dd, 4);
sun.position.set(500, 1000, 300);
sun.castShadow = true;
sun.shadow.mapSize.set(2048, 2048);
sun.shadow.camera.left = -200;
sun.shadow.camera.right = 200;
sun.shadow.camera.top = 200;
sun.shadow.camera.bottom = -200;
sun.shadow.camera.far = 2000;
scene.add(sun);

const ambient = new AmbientLight(0x4466aa, 1.5);
scene.add(ambient);

const chunkManager = new ChunkManager();
scene.add(chunkManager.group);

const player = new Player();
scene.add(player.group);

const clock = new Clock();
const targetCameraPos = new Vector3();
const camBehind = new Vector3();
const scenePlayerPos = new Vector3();

const speedEl = document.getElementById('speed')!;
const posEl = document.getElementById('pos')!;
const loadedEl = document.getElementById('loaded')!;
const loadingEl = document.getElementById('loading')!;
const barEl = document.getElementById('loading-bar')!;
const textEl = document.getElementById('loading-text')!;

window.addEventListener('keydown', (e) => {
  // Let browser/OS shortcuts through (reload, focus URL bar, devtools, etc.)
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  player.onKeyDown(e.code);
  // Only block default for keys that would scroll the page (Space / arrows)
  if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
});
window.addEventListener('keyup', (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return;
  player.onKeyUp(e.code);
});
window.addEventListener('resize', () => {
  renderer.setSize(window.innerWidth, window.innerHeight);
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
});

let gameReady = false;
let camYaw = Math.PI; // persistent camera facing; survives stops
let minimap: Minimap | null = null;

function gameLoop(): void {
  requestAnimationFrame(gameLoop);

  const dt = Math.min(clock.getDelta(), 0.1);
  if (dt <= 0 || !chunkManager.fullHeightmap) return;

  // Update terrain chunks (handles origin rebasing internally)
  const oX0 = chunkManager.worldOrigin.x;
  const oZ0 = chunkManager.worldOrigin.z;
  const tileCount = chunkManager.update(player.position.x, player.position.z);
  // Keep the camera locked to the world when the render origin rebases,
  // otherwise the whole scene would visibly jump one tile each crossing.
  const shiftX = chunkManager.worldOrigin.x - oX0;
  const shiftZ = chunkManager.worldOrigin.z - oZ0;
  if (shiftX !== 0 || shiftZ !== 0) {
    camera.position.x -= shiftX;
    camera.position.z -= shiftZ;
    targetCameraPos.x -= shiftX;
    targetCameraPos.z -= shiftZ;
  }

  // Compute scene-space player position (world pos minus world origin offset)
  const ox = chunkManager.worldOrigin.x;
  const oz = chunkManager.worldOrigin.z;
  scenePlayerPos.set(
    player.position.x - ox,
    player.position.y,
    player.position.z - oz,
  );
  player.group.position.copy(scenePlayerPos);

  if (!gameReady) {
    const total = chunkManager.totalTilesNeeded;
    const built = chunkManager.tilesBuilt;
    const pct = Math.min(100, Math.round((built / total) * 100));
    barEl.style.width = pct + '%';
    textEl.textContent = `Building terrain... ${built}/${total}`;

    // Hold the pre-placed chase camera steady (no drift from a far start pos)
    camera.position.copy(targetCameraPos);
    camera.lookAt(
      scenePlayerPos.x,
      scenePlayerPos.y + CAMERA_LOOK,
      scenePlayerPos.z,
    );

    if (built >= total) {
      gameReady = true;
      loadingEl.style.display = 'none';
    }
    renderer.render(scene, camera);
    return;
  }

  player.update(dt, chunkManager, camYaw);

  // Chase camera: follow the movement yaw (keeps a behind/above offset even
  // when stationary, so we never look straight down on the ball).
  const hSpeed = Math.hypot(player.velocity.x, player.velocity.z);
  if (hSpeed > 1.5) {
    const targetYaw = Math.atan2(player.velocity.x, player.velocity.z);
    let diff = targetYaw - camYaw;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    camYaw += diff * (1 - Math.exp(-CAMERA_SMOOTH * dt));
  }
  const fwdX = Math.sin(camYaw);
  const fwdZ = Math.cos(camYaw);
  camBehind.set(
    scenePlayerPos.x - fwdX * CAMERA_DISTANCE,
    scenePlayerPos.y + CAMERA_HEIGHT,
    scenePlayerPos.z - fwdZ * CAMERA_DISTANCE,
  );

  // Keep the camera above the terrain
  const worldCamX = camBehind.x + ox;
  const worldCamZ = camBehind.z + oz;
  const terrainAtCam = chunkManager.sampleHeight(worldCamX, worldCamZ);
  const minCamY = terrainAtCam + CAMERA_MIN_ABOVE_TERRAIN;
  if (camBehind.y < minCamY) camBehind.y = minCamY;

  targetCameraPos.lerp(camBehind, 1 - Math.exp(-CAMERA_SMOOTH * dt));
  camera.position.copy(targetCameraPos);
  camera.lookAt(
    scenePlayerPos.x,
    scenePlayerPos.y + CAMERA_LOOK,
    scenePlayerPos.z,
  );

  // Minimap
  if (minimap) {
    minimap.update(player.position.x, player.position.z, player.heading);
    minimap.render();
  }

  speedEl.textContent = `${Math.round(player.speed * 3.6)} km/h`;
  const worldW = chunkManager.getWorldWidth();
  const worldH = chunkManager.getWorldHeight();
  const dispX = ((player.position.x % worldW) + worldW) % worldW;
  const dispZ = ((player.position.z % worldH) + worldH) % worldH;
  posEl.textContent = `x: ${Math.round(dispX)} z: ${Math.round(dispZ)}`;
  loadedEl.textContent = `tiles: ${tileCount}`;

  sun.position.set(
    scenePlayerPos.x + 500,
    scenePlayerPos.y + 1000,
    scenePlayerPos.z + 300,
  );
  sun.target.position.copy(scenePlayerPos);
  sun.target.updateMatrixWorld();

  renderer.render(scene, camera);
}

async function init(): Promise<void> {
  textEl.textContent = 'Loading heightmap...';
  barEl.style.width = '10%';

  await chunkManager.loadFullHeightmap();

  // Minimap covers exactly the heightmap's world extent so the arrow aligns
  // with the terrain features shown in the backdrop image.
  minimap = new Minimap(
    chunkManager.fullW * WORLD_SCALE,
    './assets/heightmap.png',
  );
  document.getElementById('minimap')!.appendChild(minimap.canvas);

  barEl.style.width = '20%';
  textEl.textContent = 'Initializing...';

  // Spawn at world centre, resting exactly on the ground (no drop).
  const spawnX = chunkManager.getWorldWidth() / 2;
  const spawnZ = chunkManager.getWorldHeight() / 2;
  const groundY = chunkManager.sampleHeight(spawnX, spawnZ);
  player.init(spawnX, spawnZ, groundY);

  chunkManager.update(player.position.x, player.position.z);

  // Snap the camera straight to the chase position so the start view is clean
  // (no fall, no lerping-in shuffle).
  camYaw = Math.PI;
  scenePlayerPos.set(
    player.position.x - chunkManager.worldOrigin.x,
    player.position.y,
    player.position.z - chunkManager.worldOrigin.z,
  );
  const fX = Math.sin(camYaw);
  const fZ = Math.cos(camYaw);
  targetCameraPos.set(
    scenePlayerPos.x - fX * CAMERA_DISTANCE,
    scenePlayerPos.y + CAMERA_HEIGHT,
    scenePlayerPos.z - fZ * CAMERA_DISTANCE,
  );
  camera.position.copy(targetCameraPos);
  camera.lookAt(
    scenePlayerPos.x,
    scenePlayerPos.y + CAMERA_LOOK,
    scenePlayerPos.z,
  );
}

init().then(() => gameLoop());